/**
 * Sistema de toasts minimalista con Context + Framer Motion.
 *
 * Uso:
 *   const { toast } = useToast()
 *   toast.success("Flujo completado", { description: "DNI 70024754" })
 *   toast.info("Nuevo flujo iniciado")
 *   toast.error("Error al guardar")
 *
 * Los toasts aparecen arriba a la derecha, se apilan verticalmente y
 * se cierran solos a los `duration` ms (default 4000).
 */
import { createContext, useCallback, useContext, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { CheckCircle2, AlertCircle, Info, X } from "lucide-react"

const ToastContext = createContext(null)

const TIPO_META = {
  success: {
    icon: CheckCircle2,
    cls: "bg-success/10 border-success/30 text-success",
    iconCls: "text-success",
  },
  error: {
    icon: AlertCircle,
    cls: "bg-danger/10 border-danger/30 text-danger",
    iconCls: "text-danger",
  },
  info: {
    icon: Info,
    cls: "bg-brand-50 border-brand-200 text-brand-700",
    iconCls: "text-brand-600",
  },
}

let nextId = 1

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([])

  const remove = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const push = useCallback((tipo, message, opts = {}) => {
    const id = nextId++
    const duration = opts.duration ?? 4000
    setToasts((prev) => [...prev, { id, tipo, message, ...opts }])
    if (duration > 0) {
      setTimeout(() => remove(id), duration)
    }
    return id
  }, [remove])

  const toast = {
    success: (msg, opts) => push("success", msg, opts),
    error:   (msg, opts) => push("error", msg, opts),
    info:    (msg, opts) => push("info", msg, opts),
    dismiss: remove,
  }

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <Toaster toasts={toasts} onDismiss={remove} />
    </ToastContext.Provider>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    // Fallback silencioso: si algo monta useToast fuera del provider
    return { toast: { success: () => {}, error: () => {}, info: () => {}, dismiss: () => {} } }
  }
  return ctx
}

function Toaster({ toasts, onDismiss }) {
  return (
    <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2 pointer-events-none">
      <AnimatePresence initial={false}>
        {toasts.map((t) => {
          const meta = TIPO_META[t.tipo] || TIPO_META.info
          const Icon = meta.icon
          return (
            <motion.div
              key={t.id}
              layout
              initial={{ opacity: 0, y: -16, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, x: 40, scale: 0.95 }}
              transition={{ type: "spring", stiffness: 400, damping: 30 }}
              className={[
                "pointer-events-auto w-[340px] max-w-[90vw] rounded-xl border shadow-float bg-white p-3.5 flex items-start gap-3",
                meta.cls,
              ].join(" ")}
            >
              <Icon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${meta.iconCls}`} strokeWidth={2.4} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-surface-900 leading-tight">
                  {t.message}
                </div>
                {t.description && (
                  <div className="text-xs text-surface-500 mt-0.5 leading-snug">
                    {t.description}
                  </div>
                )}
              </div>
              <button
                onClick={() => onDismiss(t.id)}
                aria-label="Cerrar"
                className="flex-shrink-0 text-surface-400 hover:text-surface-700 transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </motion.div>
          )
        })}
      </AnimatePresence>
    </div>
  )
}
